import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ScanSearch, Search, Compass, Wrench, TrendingUp, ArrowRight, ShieldCheck,
  Star, Globe, Users, Gauge, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/brand/BrandLogo";

const STEPS = [
  { icon: Search, title: "Scan", desc: "We analyze your live website, business info, reputation & competitive position." },
  { icon: Compass, title: "Discover", desc: "Uncover revenue leaks, outdated info and missed opportunities you can't see." },
  { icon: Wrench, title: "Fix", desc: "Get prioritized, actionable recommendations for every finding." },
  { icon: TrendingUp, title: "Grow", desc: "Turn fixes into more calls, bookings, reviews and repeat customers." },
];

const FEATURES = [
  { icon: Globe, title: "Real Website Audit", desc: "Live analysis of speed, mobile-friendliness, SEO, broken links, CTAs, contact info and outdated years." },
  { icon: Star, title: "Reputation Insight", desc: "Understand review volume, ratings and unanswered-review opportunities — honestly, never fabricated." },
  { icon: Users, title: "Competitor Comparison", desc: "See how you stack up against local competitors on reviews, ratings and online presence." },
  { icon: Gauge, title: "Business Health Score", desc: "One AI-generated 0–100 score across 5 categories, with clear explanations for each." },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="bg-slate-50">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="mb-6" data-testid="hero-brand">
              <BrandLogo variant="wordmark" imgClass="h-12 sm:h-14 w-auto object-contain" markClass="w-10 h-10 shrink-0" textClass="text-3xl sm:text-4xl" />
              <div className="mt-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                Business Scanner
              </div>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm mb-6">
              <ShieldCheck className="w-4 h-4 text-indigo-600" /> AI business detective for local businesses
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Find the revenue and opportunities your business may be missing.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Revenue Radar analyzes your online presence, reputation, website, services and
              competitive position to uncover problems and opportunities you may be missing.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-start-scan" onClick={() => nav("/scan")}
                className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-7 py-6 text-base active:scale-95 transition-transform">
                Scan My Business Free <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <span className="text-sm text-slate-500">No signup · Results in under 60 seconds</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {["Real website analysis", "Honest, no fabricated data", "Actionable recommendations"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.25)] border border-slate-800 bg-slate-900" data-testid="hero-logo">
              <div className="grid place-items-center w-full h-[420px] px-8 pt-8 pb-24">
                <BrandLogo variant="hero" className="w-full max-w-sm" alt="Revenue Radar — Business Scanner" />
              </div>
              <div className="absolute bottom-4 left-4 right-4 backdrop-blur-xl bg-white/90 border border-white/60 rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-600 font-semibold uppercase tracking-wide">Business Health Score</div>
                    <div className="font-heading text-3xl font-bold text-slate-900">64<span className="text-base text-slate-400">/100</span></div>
                  </div>
                  <div className="flex items-center gap-2 rounded-full bg-red-50 border border-red-200 px-3 py-1.5">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs font-semibold text-red-600">3 high-priority issues</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center mb-12">
          <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">How it works</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Scan → Discover → Fix → Grow
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-200">
              <span className="absolute top-5 right-5 font-heading text-4xl font-bold text-slate-100">{i + 1}</span>
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-slate-900 text-white mb-4">
                <s.icon className="w-5 h-5" />
              </span>
              <h3 className="font-heading text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              A complete look at your business from the outside in.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              We look at your business the way a customer — and a competitor — would, then tell you what to fix first.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="flex gap-4 p-6 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-md transition-all duration-200">
                <span className="grid place-items-center w-11 h-11 rounded-lg bg-indigo-600 text-white shrink-0">
                  <f.icon className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">{f.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="rounded-2xl bg-slate-900 text-white p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative max-w-2xl">
            <ScanSearch className="w-10 h-10 text-indigo-400 mb-5" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              See your biggest opportunities — in under a minute.
            </h2>
            <p className="mt-4 text-slate-300 text-lg">
              Enter your business, run the scan, and get a professional opportunity report you can act on today.
            </p>
            <Button data-testid="cta-bottom-scan" onClick={() => nav("/scan")}
              className="mt-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-7 py-6 text-base active:scale-95 transition-transform">
              Start Your Free Scan <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
